/**
 * External dependencies
 */
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import {
	getBreadcrumbs,
	isBreadcrumb,
	type SelectorBreadcrumb,
} from './breadcrumbs';
import { getCssPath } from './css-path';
import type { Maybe } from '@nab/types';

/**
 * Internal dependencies
 */
import { getEditableContentElement } from './editable-content';
import { getElementLabel } from './element-label';

type InternalState = {
	readonly autohide: boolean;
	readonly mouseover: boolean;
	readonly hoveredElement: HTMLElement;
	readonly selector: string;
	readonly targets: ReadonlyArray< HTMLElement >;
	readonly boxes: ReadonlyArray< HTMLElement >;
	readonly result: {
		readonly numberOfElements: number;
		readonly breadcrumbs: ReadonlyArray< SelectorBreadcrumb >;
	};
};

let STATE: InternalState = {
	autohide: false,
	mouseover: false,
	hoveredElement: createHoveredBox(),
	selector: '',
	targets: [],
	boxes: [],
	result: {
		numberOfElements: 0,
		breadcrumbs: [],
	},
};

export function highlightCssSelector(
	selector: string,
	mode: 'autohide' | 'permanent'
): typeof STATE.result {
	manageHighlightersVisibility( 'autohide' === mode );

	if ( selector === STATE.selector ) {
		return STATE.result;
	}

	try {
		const elements = isIdSelector( selector )
			? [ document.querySelector< HTMLElement >( selector ) ].filter(
					( e ) => !! e
			  )
			: Array.from(
					document.querySelectorAll< HTMLElement >( selector )
			  );

		renderHighlighters( selector, elements );
		showHighlighters();
		const activeElement =
			elements.find( ( el ) =>
				el.classList.contains( 'nab-selected' )
			) || elements[ 0 ];
		if ( ! isElementAlreadyVisible( activeElement ) ) {
			activeElement?.scrollIntoView( {
				behavior: 'smooth',
				block: 'center',
			} );
		}

		STATE = {
			...STATE,
			result: {
				numberOfElements: elements.length,
				breadcrumbs: getBreadcrumbs(
					STATE.result.breadcrumbs,
					selector
				),
			},
		};
	} catch ( _ ) {
		clearHighlighters();
	}

	return STATE.result;
}

export const drawHoveredElement = (
	editableContentOnly: boolean
): ( ( ev: Event ) => void ) =>
	debounce( ( ev: Event ) => {
		if ( ! STATE.mouseover ) {
			return;
		}

		if ( ! ( ev.target instanceof HTMLElement ) ) {
			return hideHoveredBox();
		}

		if ( ev.target.nodeName === 'HTML' ) {
			return hideHoveredBox();
		}

		if (
			ev.target.classList.value.includes( 'nab-highlight' ) ||
			ev.target.classList.contains( 'nab-selected' )
		) {
			return hideHoveredBox();
		}

		if ( isBreadcrumb( ev.target ) ) {
			return hideHoveredBox();
		}

		let el: Maybe< HTMLElement > = ev.target;
		if ( editableContentOnly ) {
			el = getEditableContentElement( ev.target );
			if ( ! el ) {
				return hideHoveredBox();
			}
		}

		const selector = getCssPath( el ) || '';
		const selectorName = getElementLabel( selector );
		STATE.hoveredElement.style.setProperty(
			'--nab-selector',
			`'${ selector }'`
		);
		STATE.hoveredElement.style.setProperty(
			'--nab-selector-name',
			`'${ selectorName ?? selector }'`
		);
		positionElementBox( STATE.hoveredElement, el.getBoundingClientRect() );
	}, 0 );

// ===============
// EVENT LISTENERS
// ===============

let reacting = false;
export function reactToEvents(): void {
	if ( reacting ) {
		return;
	}
	reacting = true;

	const refreshHighlighters = throttle(
		() => renderHighlighters( STATE.selector, STATE.targets ),
		200
	);

	document.addEventListener(
		'nab/css-selector-finder/refresh-highlighters',
		() => {
			refreshHighlighters();
		}
	);

	window.addEventListener( 'resize', refreshHighlighters );

	window.addEventListener( 'scroll', repositionTooltips );
}

// =======
// HELPERS
// =======

const setHasMouse = ( mouseover: boolean ) => {
	STATE = { ...STATE, mouseover };
};
document.body.addEventListener( 'mouseenter', () => setHasMouse( true ) );
document.body.addEventListener( 'mousemove', () => setHasMouse( true ) );
document.body.addEventListener( 'mouseleave', () => setHasMouse( false ) );
document.body.addEventListener( 'mouseleave', () => hideHoveredBox() );

function manageHighlightersVisibility( autohide: boolean ): void {
	if ( STATE.autohide === autohide ) {
		return;
	}

	STATE = { ...STATE, autohide };
	if ( ! autohide ) {
		document.body.removeEventListener( 'mouseenter', showHighlighters );
		document.body.removeEventListener(
			'mouseleave',
			setHighlighterHideTimer
		);
		return;
	}

	document.body.addEventListener( 'mouseenter', showHighlighters );
	document.body.addEventListener( 'mouseleave', setHighlighterHideTimer );
}

const isIdSelector = ( s: string ) => /^#[^\s]+$/.test( s );

function showHighlighters(): void {
	clearTimeout( hideTimeout );
	STATE.boxes.forEach( ( highlighter ) =>
		highlighter.classList.remove( 'nab-highlight--hidden' )
	);
	if ( STATE.autohide && ! STATE.mouseover ) {
		setHighlighterHideTimer();
	}
}

let hideTimeout: NodeJS.Timeout;
function setHighlighterHideTimer(): void {
	hideTimeout = setTimeout( () => {
		STATE.boxes.forEach( ( highlighter ) =>
			highlighter.classList.add( 'nab-highlight--hidden' )
		);
	}, 2000 );
}

const hideHoveredBox = debounce( (): void => {
	STATE.hoveredElement.style.display = 'none';
	STATE.hoveredElement.style.top = '50%';
	STATE.hoveredElement.style.left = '50%';
	STATE.hoveredElement.style.width = '0';
	STATE.hoveredElement.style.height = '0';
}, 0 );

function renderHighlighters(
	selector: string,
	elements: ReadonlyArray< HTMLElement >
) {
	clearHighlighters();

	STATE = {
		...STATE,
		selector,
		targets: elements,
		boxes: elements
			.map( ( element ) => createHighlightedBox( selector, element ) )
			.filter( ( e ) => !! e ),
		result: {
			numberOfElements: elements.length,
			breadcrumbs: getBreadcrumbs( STATE.result.breadcrumbs, selector ),
		},
	};

	repositionTooltips();
}

function clearHighlighters(): void {
	STATE.boxes.forEach(
		( highlighter ) => highlighter.parentElement?.removeChild( highlighter )
	);
	STATE = {
		...STATE,
		selector: '',
		boxes: [],
		targets: [],
		result: { numberOfElements: 0, breadcrumbs: [] },
	};
}

function positionElementBox( box: HTMLElement, rect: DOMRect ) {
	if ( ! box.style ) {
		return;
	}

	box.style.display = 'block';
	box.style.top = `${ Math.floor( rect.top ) - 1 + window.scrollY }px`;
	box.style.left = `${ Math.floor( rect.left ) - 1 + window.scrollX }px`;
	box.style.width = `${ rect.width }px`;
	box.style.height = `${ rect.height }px`;

	repositionTooltip( box );
}

function repositionTooltips() {
	[ STATE.hoveredElement, ...STATE.boxes ].forEach( repositionTooltip );
}

function repositionTooltip( box: HTMLElement ) {
	const tooltip = box.firstElementChild as HTMLElement | null;
	if ( ! tooltip ) {
		return;
	}

	const rect = box.getBoundingClientRect();

	const viewportHeight = window.innerHeight;
	const viewportTop = window.scrollY;
	const viewportBottom = viewportTop + viewportHeight;
	const boxTop = Math.floor( rect.top ) - 1 + window.scrollY;
	const boxBottom = boxTop + rect.height;
	const boxHeight = rect.height;
	const tooltipHeight = tooltip.offsetHeight;

	if ( boxBottom <= viewportTop ) {
		tooltip.style.top = `${ boxHeight - 3 + viewportTop - boxBottom }px`;
		tooltip.style.borderRadius = '0 0 5px 0';
	} else if ( boxTop >= viewportBottom ) {
		tooltip.style.borderRadius = '0 5px 0 0';
		tooltip.style.top = `-${
			tooltipHeight + 3 + boxTop - viewportBottom
		}px`;
	} else if (
		boxTop - tooltipHeight >= viewportTop &&
		boxTop <= viewportBottom
	) {
		tooltip.style.top = `-${ tooltipHeight }px`;
		tooltip.style.borderRadius = '0 5px 0 0';
	} else if ( boxTop - tooltipHeight >= viewportTop ) {
		tooltip.style.top = `-${
			tooltipHeight + 3 + boxTop - viewportBottom
		}px`;
		tooltip.style.borderRadius = '0 5px 0 0';
	} else if ( boxBottom + tooltipHeight <= viewportBottom ) {
		tooltip.style.top = `${ boxHeight - 2 }px`;
		tooltip.style.borderRadius = '0 0 5px 0';
	} else {
		tooltip.style.top = `${ viewportTop - boxTop - 3 }px`;
		tooltip.style.borderRadius = '0 0 5px 0';
	}
}

function createHoveredBox() {
	const box = document.createElement( 'div' );
	box.className = 'nab-hovered';

	const tooltip = document.createElement( 'div' );
	tooltip.className = 'nab-hovered__tooltip';
	const tooltipText = document.createElement( 'span' );
	tooltipText.className = 'nab-hovered__tooltip-text';
	tooltip.appendChild( tooltipText );
	box.appendChild( tooltip );

	document.body.appendChild( box );
	return box;
}

function createHighlightedBox(
	selector: string,
	element: HTMLElement
): HTMLElement | undefined {
	const rect = element.getBoundingClientRect();
	if ( rect.width === 0 || rect.height === 0 ) {
		return;
	}

	const isSecondary = ! element.classList.contains( 'nab-selected' );
	const highlighter = document.createElement( 'div' );
	highlighter.className = 'nab-highlight'
		.concat( isSecondary ? ' nab-highlight--secondary' : '' )
		.concat(
			STATE.autohide && ! STATE.mouseover ? ' nab-highlight--hidden' : ''
		);
	highlighter.style.setProperty( '--nab-selector', `'${ selector }'` );
	highlighter.style.setProperty(
		'--nab-selector-name',
		`'${ getElementLabel( getCssPath( element ) || selector ) }'`
	);

	const highlighterTooltip = document.createElement( 'div' );
	highlighterTooltip.className = 'nab-highlight__tooltip';
	const highlighterTooltipText = document.createElement( 'span' );
	highlighterTooltipText.className = 'nab-highlight__tooltip-text';
	highlighterTooltip.appendChild( highlighterTooltipText );
	highlighter.appendChild( highlighterTooltip );

	document.body.appendChild( highlighter );

	positionElementBox( highlighter, rect );
	return highlighter;
}

function isElementAlreadyVisible( element?: HTMLElement ): boolean {
	if ( ! element ) {
		return false;
	}

	const rect = element.getBoundingClientRect();
	const visibleHeight =
		Math.min( rect.bottom, window.innerHeight ) - Math.max( rect.top, 0 );

	const windowHeight = window.innerHeight;
	const elementToScreenRatio = visibleHeight / windowHeight;
	if ( elementToScreenRatio >= 0.75 ) {
		return true;
	}

	const elementVisibleRatio = visibleHeight / rect.height;
	return elementVisibleRatio >= 0.75;
}
